import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ApiService from "../utils/fetch";

// Async thunks for API calls
export const fetchDropdownData = createAsyncThunk(
  "realignment/fetchDropdowns",
  async (_, { rejectWithValue }) => {
    try {
      const response = await ApiService.get(
        "/v1/realignment/m0/dropdowns/all"
      );
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchRealignmentRecords = createAsyncThunk(
  "realignment/fetchRecords",
  async (
    {
      page = 0,
      size = 10,
      status,
      searchTerm,
      sortBy,
      sortDir,
    } = {},
    { rejectWithValue },
  ) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.set("page", String(page));
      queryParams.set("size", String(size));

      if (status) queryParams.set("status", status);
      if (searchTerm) queryParams.set("searchTerm", searchTerm);
      if (sortBy) queryParams.set("sortBy", sortBy);
      if (sortDir) queryParams.set("sortDir", sortDir);

      const response = await ApiService.get(
        `/v1/realignment/records?${queryParams.toString()}`
      );
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateRealignmentStatus = createAsyncThunk(
  "realignment/updateStatus",
  async ({ projectId, action }, { rejectWithValue }) => {
    try {
      const response = await ApiService.post(
        `/v1/realignment/records/${projectId}/status`,
        { action }
      );
      if (response.data.success) {
        return { projectId, action, ...response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateRealignmentRecord = createAsyncThunk(
  "realignment/updateRecord",
  async ({ recordId, updateData }, { rejectWithValue }) => {
    try {
      const response = await ApiService.put(
        `/v1/realignment/records/${recordId}`,
        updateData
      );
      if (response.data.success) {
        return { recordId, ...response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const approveBatchRealignment = createAsyncThunk(
  "realignment/approveBatch",
  async ({ recordIds }, { rejectWithValue }) => {
    try {
      const response = await ApiService.post(
        `/v1/realignment/approve/batch`,
        { recordIds }
      );
      if (response.data.success) {
        return { recordIds, ...response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const approveBatchMasterData = createAsyncThunk(
  "realignment/approveBatchMasterData",
  async ({ recordIds }, { rejectWithValue }) => {
    try {
      const response = await ApiService.post(
        `/v1/realignment/approve/master-data/batch`,
        { recordIds }
      );
      // Axios throws on non-2xx; reaching here means success.
      // API returns { totalProcessed, totalSuccess, results, ... } with no top-level success field.
      return { recordIds, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const rejectBulkRealignment = createAsyncThunk(
  "realignment/rejectBulk",
  async ({ ids, rejectionRemarks }, { rejectWithValue }) => {
    try {
      const response = await ApiService.post(
        `/v1/realignment/reject/bulk`,
        { ids, rejectionRemarks }
      );
      if (response.data.success) {
        return { ids, rejectionRemarks, ...response.data.data };
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  // Realignment records data
  pendingRecords: [],
  actionedRecords: [],
  currentPage: 0,
  pageSize: 10,
  totalElements: 0,
  totalPages: 0,
  lastPage: false,

  // Dropdown data
  dropdownData: {
    locations: [],
    profitCenters: [],
    forecastPartners: [],
  },

  // Loading and error states
  loading: false,
  dropdownLoading: false,
  savingRecord: false,
  error: null,
  successMessage: null,
  saveStatus: null, // "success" or error message
  saveStatusType: null, // "success" or "error"

  // Selection and approval tracking
  selectedRecords: [],
  mdApprovalStatus: {}, // Maps recordId to MD approval status
};

const realignmentSlice = createSlice({
  name: "realignment",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearSaveStatus: (state) => {
      state.saveStatus = null;
      state.saveStatusType = null;
    },
    setSelectedRecords: (state, action) => {
      state.selectedRecords = action.payload;
    },
    setMdApprovalStatus: (state, action) => {
      const { recordId, status } = action.payload;
      state.mdApprovalStatus[recordId] = status;
    },
    // Update local state for optimistic updates before API call
    updateEditedValues: (state, action) => {
      const { projectId, fieldName, value } = action.payload;
      const recordIndex = state.pendingRecords.findIndex(
        (r) => r.id === projectId
      );
      if (recordIndex !== -1) {
        state.pendingRecords[recordIndex] = {
          ...state.pendingRecords[recordIndex],
          [fieldName]: value,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch dropdowns
    builder
      .addCase(fetchDropdownData.pending, (state) => {
        state.dropdownLoading = true;
        state.error = null;
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        state.dropdownLoading = false;
        state.dropdownData = action.payload;
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.dropdownLoading = false;
        state.error = action.payload;
      });

    // Fetch records
    builder
      .addCase(fetchRealignmentRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRealignmentRecords.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingRecords = action.payload.content || [];
        state.currentPage = action.payload.page || 0;
        state.pageSize = action.payload.size ?? 10;
        state.totalElements = action.payload.totalElements || 0;
        state.totalPages = action.payload.totalPages || 0;
        state.lastPage = action.payload.lastPage || false;
      })
      .addCase(fetchRealignmentRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update status
    builder
      .addCase(updateRealignmentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRealignmentStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { projectId, action: actionType } = action.payload;
        
        // Move record from pending to actioned
        const recordIndex = state.pendingRecords.findIndex(
          (r) => r.flowProjectId === projectId || r.id === projectId
        );
        if (recordIndex !== -1) {
          const movedRecord = state.pendingRecords.splice(recordIndex, 1)[0];
          const currentDate = new Date().toISOString().split("T")[0];
          movedRecord.status = actionType === "APPROVE" ? "APPROVED" : "REJECTED";
          movedRecord.approvedDate = currentDate;
          state.actionedRecords.push(movedRecord);
          state.successMessage = `Record ${actionType === "APPROVE" ? "approved" : "rejected"} successfully`;
        }
      })
      .addCase(updateRealignmentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update realignment record
    builder
      .addCase(updateRealignmentRecord.pending, (state) => {
        state.savingRecord = true;
        state.saveStatus = null;
        state.saveStatusType = null;
      })
      .addCase(updateRealignmentRecord.fulfilled, (state, action) => {
        state.savingRecord = false;
        const { recordId } = action.payload;
        
        // Update the record in pendingRecords
        const recordIndex = state.pendingRecords.findIndex((r) => r.id === recordId);
        if (recordIndex !== -1) {
          state.pendingRecords[recordIndex] = {
            ...state.pendingRecords[recordIndex],
            ...action.payload,
          };
        }
        
        state.saveStatus = "Record saved successfully";
        state.saveStatusType = "success";
      })
      .addCase(updateRealignmentRecord.rejected, (state, action) => {
        state.savingRecord = false;
        state.saveStatus = action.payload || "Failed to save record";
        state.saveStatusType = "error";
      });

    // Approve batch realignment
    builder
      .addCase(approveBatchRealignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveBatchRealignment.fulfilled, (state, action) => {
        state.loading = false;
        const { recordIds } = action.payload;

        recordIds.forEach((id) => {
          const recordIndex = state.pendingRecords.findIndex(
            (r) => r.id === id || r.flowProjectId === id,
          );
          if (recordIndex !== -1) {
            const approvedRecord = state.pendingRecords.splice(recordIndex, 1)[0];
            approvedRecord.status = "APPROVED";
            approvedRecord.approvedDate = new Date().toISOString().split("T")[0];
            state.actionedRecords.push(approvedRecord);
          }
        });

        state.successMessage = `${recordIds.length} record(s) approved successfully`;
      })
      .addCase(approveBatchRealignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Approve batch master data
    builder
      .addCase(approveBatchMasterData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveBatchMasterData.fulfilled, (state, action) => {
        state.loading = false;
        const { recordIds, results = [] } = action.payload;

        // Update mdApprovalStatus and patch masterDataApprovalStatus in pendingRecords
        // using the per-record finalStatus from the API response.
        recordIds.forEach((id) => {
          const result = results.find((r) => r.id === id);
          const finalStatus = result?.finalStatus || "APPROVED";
          state.mdApprovalStatus[id] = finalStatus === "APPROVED";

          const recordIndex = state.pendingRecords.findIndex((r) => r.id === id);
          if (recordIndex !== -1) {
            state.pendingRecords[recordIndex] = {
              ...state.pendingRecords[recordIndex],
              masterDataApprovalStatus: finalStatus,
            };
          }
        });

        state.successMessage = `${action.payload.totalSuccess ?? recordIds.length} record(s) master data approved successfully`;
      })
      .addCase(approveBatchMasterData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Reject bulk realignment
    builder
      .addCase(rejectBulkRealignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectBulkRealignment.fulfilled, (state, action) => {
        state.loading = false;
        const { ids } = action.payload;
        
        // Remove rejected records or move them to actioned
        ids.forEach((id) => {
          const recordIndex = state.pendingRecords.findIndex((r) => r.id === id);
          if (recordIndex !== -1) {
            const rejectedRecord = state.pendingRecords.splice(recordIndex, 1)[0];
            rejectedRecord.status = "REJECTED";
            state.actionedRecords.push(rejectedRecord);
          }
        });
        
        state.successMessage = `${ids.length} record(s) rejected successfully`;
      })
      .addCase(rejectBulkRealignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSuccessMessage, clearSaveStatus, setSelectedRecords, setMdApprovalStatus, updateEditedValues } =
  realignmentSlice.actions;
export default realignmentSlice.reducer;
